/**
 * ContractEstimateSlice Selectors
 * 
 * Memoized selectors for accessing contract estimate slice state.
 * Uses createSelector for performance optimization.
 */

import { createSelector } from '@reduxjs/toolkit';
import { RootState } from '../../../app/store';
import { ContractEstimateSliceState } from './state';

// Base selector to get the contract estimate slice
const selectContractEstimateSlice = (state: RootState): ContractEstimateSliceState => state.contractEstimate;

// =============================================================================
// CORE SELECTORS
// =============================================================================

/**
 * Select the current estimate entity
 */
export const selectCurrentEstimate = createSelector(
  selectContractEstimateSlice,
  (slice) => slice.currentEstimate
);

/**
 * Select whether current estimate exists
 */
export const selectHasCurrentEstimate = createSelector(
  selectCurrentEstimate,
  (estimate) => estimate !== null
);

/**
 * Select the current estimate ID (if exists)
 */
export const selectCurrentEstimateId = createSelector(
  selectCurrentEstimate,
  (estimate) => estimate?.id
);

// =============================================================================
// SAVED ESTIMATES SELECTORS
// =============================================================================

/**
 * Select all saved estimates
 */
export const selectSavedEstimates = createSelector(
  selectContractEstimateSlice,
  (slice) => slice.savedEstimates
);

/**
 * Select the count of saved estimates
 */
export const selectSavedEstimatesCount = createSelector(
  selectSavedEstimates,
  (estimates) => estimates.length
);

/**
 * Factory selector for a specific saved estimate by ID
 */
export const selectSavedEstimateById = (estimateId: string) =>
  createSelector(
    selectSavedEstimates,
    (estimates) => estimates.find((est) => est.id === estimateId)
  );

// =============================================================================
// OPTIONS SELECTORS
// =============================================================================

/**
 * Select available centers
 */
export const selectAvailableCenters = createSelector(
  selectContractEstimateSlice,
  (slice) => slice.availableCenters
);

/**
 * Select available payers
 */
export const selectAvailablePayers = createSelector(
  selectContractEstimateSlice,
  (slice) => slice.availablePayers
);

// =============================================================================
// UI STATE SELECTORS
// =============================================================================

/**
 * Select loading state
 */
export const selectIsLoading = createSelector(
  selectContractEstimateSlice,
  (slice) => slice.isLoading
);

/**
 * Select calculating state (when API call is in progress)
 */
export const selectIsCalculating = createSelector(
  selectContractEstimateSlice,
  (slice) => slice.isCalculating
);

/**
 * Select whether there are unsaved changes
 */
export const selectIsDirty = createSelector(
  selectContractEstimateSlice,
  (slice) => slice.isDirty
);

// =============================================================================
// VALIDATION SELECTORS
// =============================================================================

/**
 * Select all validation errors
 */
export const selectValidationErrors = createSelector(
  selectContractEstimateSlice,
  (slice) => slice.validationErrors
);

/**
 * Select whether there are any validation errors
 */
export const selectHasValidationErrors = createSelector(
  selectValidationErrors,
  (errors) => Object.keys(errors).length > 0
);

/**
 * Factory selector for a specific validation error
 */
export const selectValidationErrorByField = (fieldName: string) =>
  createSelector(
    selectValidationErrors,
    (errors) => errors[fieldName]
  );

// =============================================================================
// PROCEDURE CODE SELECTORS
// =============================================================================

/**
 * Select procedure codes from current estimate
 */
export const selectProcedureCodes = createSelector(
  selectCurrentEstimate,
  (estimate) => estimate?.procedureCodes ?? []
);

/**
 * Select procedure codes count
 */
export const selectProcedureCodesCount = createSelector(
  selectProcedureCodes,
  (codes) => codes.length
);

// =============================================================================
// TOTALS SELECTORS
// =============================================================================

/**
 * Select total reimbursement from current estimate
 */
export const selectTotalReimbursement = createSelector(
  selectCurrentEstimate,
  (estimate) => estimate?.totalReimbursement ?? 0
);

/**
 * Select total implant cost from current estimate
 */
export const selectTotalImplantCost = createSelector(
  selectCurrentEstimate,
  (estimate) => estimate?.totalImplantCost ?? 0
);

/**
 * Select grand total from current estimate
 */
export const selectGrandTotal = createSelector(
  selectCurrentEstimate,
  (estimate) => estimate?.grandTotal ?? 0
);

// =============================================================================
// CALCULATION RESULT SELECTORS
// =============================================================================

/**
 * Select calculation result from current estimate
 */
export const selectCalculationResult = createSelector(
  selectCurrentEstimate,
  (estimate) => estimate?.calculationResult
);

/**
 * Select whether a calculation result exists
 */
export const selectHasCalculationResult = createSelector(
  selectCalculationResult,
  (result) => result !== undefined && result !== null
);

/**
 * Select contract resolution from calculation result
 */
export const selectContractResolution = createSelector(
  selectCalculationResult,
  (result) => result?.contractResolution
);

/**
 * Select procedure results from calculation result
 */
export const selectProcedureResults = createSelector(
  selectCalculationResult,
  (result) => result?.procedureResults ?? []
);

/**
 * Select calculation audit trail from calculation result
 */
export const selectCalculationAuditTrail = createSelector(
  selectCalculationResult,
  (result) => result?.auditTrail ?? []
);

/**
 * Select calculated total reimbursement
 */
export const selectCalculatedTotalReimbursement = createSelector(
  selectCalculationResult,
  (result) => result?.totalReimbursement ?? 0
);

/**
 * Select calculated total charges
 */
export const selectCalculatedTotalCharges = createSelector(
  selectCalculationResult,
  (result) => result?.totalBilledCharges ?? 0
);
